import { useState } from 'react'
import { useRouter } from 'next/router'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import { FormProvider, useForm } from 'react-hook-form'

import api from 'lib/api'
import { useStore } from 'lib/store'
import { useToast } from 'hooks/useToast'

import { VacancyType } from 'types/vacancy'
import { Apply, applyVacancyInJobs } from '../utils'
import {
  Button,
  IconProfilerGroup,
  ModalAction,
  ModalRecommendation
} from 'components'
import {
  recommendationModalSchema,
  defaultValues
} from 'utils/form/recommendation-modal'
import { handleToAuth } from 'services/user'
import { getMetricName } from 'utils/page'

interface Props {
  id: number
  title: string
  type: VacancyType
  verifyRequiredFields: boolean
  pcdOnly: boolean
  onSuccess: () => void
}

type ModalType =
  | 'review-curriculum'
  | 'internal-apply'
  | 'external-apply'
  | 'form-recommendation'
  | 'vacancy-pcd'
  | 'duplicate-acount'
  | 'without-profiler'
  | 'complete-required-fields'

const alertModals = {
  'internal-apply': {
    icon: 'business_center',
    title: 'Candidatura Interna',
    description:
      'Esta vaga foi planejada apenas para os colaboradores desta empresa. Caso você seja um colaborador, entre em contato com o seu RH.'
  },
  'external-apply': {
    icon: 'business_center',
    title: 'Candidatura Externa',
    description:
      'Você não pode se candidatar a esta vaga, por ser colaborador desta empresa e a vaga trata-se de uma candidatura externa.'
  },
  'vacancy-pcd': {
    icon: 'accessible',
    title: 'Vaga PCD',
    description:
      'Vaga destinada apenas para pessoas PCD (Pessoas com Deficiência). Portanto não é possível se candidatar'
  }
}

export const CandidatureButton = ({
  verifyRequiredFields,
  onSuccess,
  ...vacancy
}: Props) => {
  const toast = useToast()
  const router = useRouter()
  const queryClient = useQueryClient()

  const { company, user, updateStoreUser } = useStore(store => store)
  const [modal, setModal] = useState<ModalType>(null)
  const [errorDetail, setErrorDetail] = useState<string | null>(null)
  const [loadingApply, setLoadingApply] = useState<boolean>(false)
  const [applyStatus, setApplyStatus] = useState<ModalType>(null)

  const useFormMethods = useForm({
    mode: 'all',
    defaultValues,
    resolver: yupResolver(recommendationModalSchema)
  })

  const handleClickHeaderButton = async () => {
    if (!user) {
      handleToAuth()
      return
    } else if (applyStatus) {
      setModal(applyStatus)
      return
    }

    try {
      if (vacancy.type !== 'mista') {
        const route = `/applicants/check-employee/${vacancy.id}`
        // eslint-disable-next-line prettier/prettier
        const response = await api<{ isActiveEmployee: boolean }>(
          route,
          {},
          'applicants'
        )
        const isEmployee = response.data.isActiveEmployee

        if (!isEmployee && vacancy.type === 'interna') {
          setModal('internal-apply')
          setApplyStatus('internal-apply')
          return
        } else if (isEmployee && vacancy.type === 'externa') {
          setModal('external-apply')
          setApplyStatus('external-apply')
          return
        }
      }

      if (verifyRequiredFields) {
        setLoadingApply(true)

        const route = '/home/vacancy/enabledtoapply'
        const params = { id: vacancy.id, email: user.email }
        const response = await api<string[]>(route, { params })

        setLoadingApply(false)

        if (response.data.length) {
          setModal('review-curriculum')
          setApplyStatus('review-curriculum')

          return
        }
      }

      setModal('form-recommendation')
    } catch (err) {
      console.error(err)
      console.log(err?.response?.message)
      toast.error({ title: 'Falha ao realizar a candidatura!' })
    } finally {
      setLoadingApply(false)
    }
  }

  const getApplyData = async (
    goCurriculum = false
  ): Promise<Apply | string> => {
    const source = router.query?.origem as string
    const applicationSource = getMetricName()

    if (goCurriculum) {
      const params = new URLSearchParams()
      params.append('vacancyId', String(vacancy.id))
      params.append('applicationSource', applicationSource)
      source && params.append('source', source)

      return params.toString()
    }

    const apply = {
      vacancy: { id: vacancy.id, title: vacancy.title, source },
      company: { name: company.name, slug: company.slug },
      applicationSource,
      recommendations: null
    }

    const formData = useFormMethods.getValues()

    if (formData.isConfirmed === 'sim') {
      const isValid = await useFormMethods.trigger()

      if (!isValid) {
        return null
      }

      apply.recommendations = {
        name: formData.name,
        email: formData.email
      }
    }

    return apply
  }

  const handleReviewCurriculum = async () => {
    const paramsApply = (await getApplyData(true)) as string
    applyVacancyInJobs(paramsApply)
  }

  const handleApplyVacancy = async () => {
    const apply = (await getApplyData()) as Apply

    if (apply) {
      const data = {
        ...apply,
        vacancyId: apply.vacancy.id,
        source: apply.vacancy.source
      }

      setLoadingApply(true)
      setModal(null)

      try {
        const route = '/applicants/apply-job'
        await api(route, { method: 'POST', data }, 'applicants')

        queryClient.setQueryData(['vacancy-applied', vacancy.id], true)
        updateStoreUser({ appliedTalentBank: true })

        onSuccess()
      } catch (err) {
        const status = err?.response?.status
        const errMessage = err?.response?.data?.title as string

        const mapperTypeModals = {
          409: 'duplicate-acount',
          401: 'without-profiler'
        }

        let typeModal = mapperTypeModals[status]

        if (status === 400) {
          if (vacancy.pcdOnly) {
            typeModal = 'vacancy-pcd'
          } else if (errMessage.includes('Cadastro incompleto')) {
            typeModal = 'complete-required-fields'
          }
        }

        if (status === 409) {
          setErrorDetail(errMessage.split(':')[1].trim())
        }

        if (typeModal) {
          setModal(typeModal)
          setApplyStatus(typeModal)
        } else {
          toast.error({ title: errMessage || 'Falha ao concluir candidatura' })
        }
      }

      setLoadingApply(false)
    }
  }

  const handleApplyWithProfiler = async () => {
    const paramsApply = (await getApplyData(true)) as string
    applyVacancyInJobs(paramsApply, true)
  }

  const handleGoToSupport = () => {
    const url =
      'https://ajuda.solides.com.br/hc/pt-br/categories/4411290914189-Sou-candidato-a-'
    window.open(url, '_blank').focus()
  }

  return (
    <>
      <FormProvider {...useFormMethods}>
        <ModalRecommendation
          onClose={() => setModal(null)}
          open={modal === 'form-recommendation'}
          onReviewCurriculum={handleReviewCurriculum}
          onApplyVacancy={handleApplyVacancy}
        />
      </FormProvider>

      <ModalAction
        icon="offline_bolt"
        open={modal === 'review-curriculum'}
        title={
          <>
            Você está a 1 passo
            <br />
            da sua candidatura
          </>
        }
        description="Para concluir sua candidatura você deverá revisar seu currículo e preencher os campos obrigatórios exigidos pela vaga. Deseja revisar seu currículo?"
        cancelButton={{ action: () => setModal(null) }}
        confirmButton={{ action: handleReviewCurriculum }}
      />

      <ModalAction
        icon="help"
        open={modal === 'duplicate-acount'}
        title="Não é possível se candidatar a vagas dessa empresa"
        description={`O CPF informado já está cadastrado em outra conta com o e-mail ${errorDetail}. Para continuar, você precisa excluir uma das contas ou entrar em contato com o suporte.`}
        cancelButton={{ action: () => setModal(null) }}
        confirmButton={{ action: handleGoToSupport, label: 'Suporte' }}
      />

      <ModalAction
        iconVariant="round"
        title=""
        open={!!alertModals[modal]}
        confirmButton={{ action: () => setModal(null), label: 'Ok, entendi!' }}
        {...alertModals[modal]}
      />

      <ModalAction
        size="400px"
        icon={<IconProfilerGroup />}
        open={modal === 'without-profiler'}
        title="Complete o Profiler para concluir sua candidatura"
        description="Antes de finalizar sua candidatura, precisamos que você complete o Profiler. É rápido e simples!"
        confirmButton={{
          action: () => handleApplyWithProfiler(),
          label: 'Fazer agora'
        }}
        cancelButton={{ action: () => setModal(null), label: 'Agora não' }}
      />

      <ModalAction
        icon="offline_bolt"
        iconVariant="round"
        open={modal === 'complete-required-fields'}
        title="Você está a alguns passos da sua candidatura"
        description="Para concluir sua candidatura você deverá revisar seu currículo e preencher os campos obrigatórios exigidos pela vaga. Deseja revisar seu currículo?"
        cancelButton={{ action: () => setModal(null) }}
        confirmButton={{
          action: handleReviewCurriculum,
          label: 'Revisar'
        }}
      />

      <Button
        colorCompany
        className="py-3 w-full sm:w-auto"
        onClick={handleClickHeaderButton}
        loading={loadingApply && 'Candidatando...'}
      >
        Quero me candidatar
      </Button>
    </>
  )
}
